export default class AccessibilityUtilities {
  public static get focusableChildSelector(): string {
    return `a[href], button, input, textarea, select, details, video, iframe, table, [tabindex]:not([tabindex="-1"]), [role='button'], .swiper-pagination .swiper-pagination-bullet`;
  }

  public static init() {
    this.disableTabbingWithinHiddenElements();
  }

  /**
   * Utility function for converting anchors simulating buttons as no-js fallbacks
   * into actual buttons when JS is enabled.
   */
  public static convertAnchorToButton(
    anchor: HTMLAnchorElement,
  ): HTMLButtonElement {
    if (!anchor) {
      throw new Error("The provided anchor element is null or undefined.");
    }

    const button = document.createElement("BUTTON") as HTMLButtonElement;

    // Copy attributes from anchor to button
    for (let i = 0; i < anchor.attributes.length; i++) {
      const attribute = anchor.attributes[i];
      // Exclude irrelevant attributes from being added to button
      if (attribute.name !== "href") {
        if (attribute.name === "role" && attribute.value === "button") {
          continue;
        }
        button.setAttribute(attribute.name, attribute.value);
      }
    }

    // Copy child elements from anchor to button
    for (let i = 0; i < anchor.childNodes.length; i++) {
      button.appendChild(anchor.childNodes[i].cloneNode(true));
    }

    // Replace the anchor with the button in the DOM
    anchor.parentElement?.replaceChild(button, anchor);

    return button; // Ensure the button is returned
  }

  /**
   * Use a MutationObserver to watch for elements with aria-hidden or hidden attribute changes
   * and deterministically update tabindex for focusable children of the target element.
   */
  private static disableTabbingWithinHiddenElements() {
    const observer = new MutationObserver((mutations: MutationRecord[]) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          ["aria-hidden", "hidden"].includes(mutation.attributeName!) &&
          mutation.target.nodeType === Node.ELEMENT_NODE
        ) {
          const target = mutation.target as HTMLElement;
          const isHidden =
            (mutation.attributeName === "hidden" &&
              target.hasAttribute("hidden")) ||
            (mutation.attributeName === "aria-hidden" &&
              target.getAttribute("aria-hidden") === "true");

          const focusableChildren = target.querySelectorAll(
            this.focusableChildSelector,
          ) as NodeListOf<HTMLElement>;

          focusableChildren.forEach((child) => {
            // Ensure changes apply only to direct descendants of the mutated target
            if (child.closest("[aria-hidden]") === target) {
              setTimeout(() => {
                child.tabIndex = isHidden ? -1 : 0;
              }, 100);
            }
          });

          // If aria-hidden=true element has children that are aria-hidden=false set to hidden
          if (isHidden) {
            // Find any children with aria-hidden="false"
            const nestedVisibleChildren = target.querySelectorAll(
              "[aria-hidden='false']",
            ) as NodeListOf<HTMLElement>;

            nestedVisibleChildren.forEach((child) => {
              // Store original state using a data attribute
              child.setAttribute("data-original-aria-hidden", "false");
              child.setAttribute("aria-hidden", "true");
            });
          } else {
            // Only restore elements that have our data attribute
            const modifiedChildren = target.querySelectorAll(
              "[data-original-aria-hidden]",
            ) as NodeListOf<HTMLElement>;

            modifiedChildren.forEach((child) => {
              const originalValue = child.getAttribute(
                "data-original-aria-hidden",
              );
              child.setAttribute("aria-hidden", originalValue || "true");
              child.removeAttribute("data-original-aria-hidden");
            });
          }
        }
      });
    });

    observer.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeOldValue: true,
    });
  }
}
